import React, { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
  ClipboardCheck,
  FileText,
  Shield,
  ClipboardList,
  Eye,
  Crown,
  ArrowUpRight,
} from "lucide-react";
import { useApp } from "../../context/AppContext";
import {
  FormSubmissionsPage,
  PIA_CONFIG,
  ROPA_CONFIG,
  TPRM_CONFIG,
  withAdminMonitorConfig,
} from "../../components/common/FormSubmissionsPage";
import { GapAssessmentResponsesPanel } from "../user/GapAssessmentSubmissions";

const TAB_MODULE = {
  gap_assessment: "gap",
  ropa: "ropa",
  pia: "dpia",
  tprm: "tprm",
};

const TABS = [
  {
    id: "gap_assessment",
    label: "Gap Assessment",
    icon: ClipboardCheck,
    iconColor: "text-indigo-500",
    description:
      "DPDP gap assessment answers, scores, comments, and evidence per department.",
  },
  {
    id: "ropa",
    label: "ROPA",
    icon: FileText,
    iconColor: "text-emerald-500",
    description: "Records of Processing Activities submitted by users.",
  },
  {
    id: "pia",
    label: "PIA",
    icon: Shield,
    iconColor: "text-violet-500",
    description: "Privacy Impact Assessment responses and risk scores.",
  },
  {
    id: "tprm",
    label: "TPRM",
    icon: ClipboardList,
    iconColor: "text-cyan-500",
    description: "Third-party risk questionnaire answers and section scores.",
  },
];

export default function FormResponsesMonitor() {
  const [searchParams] = useSearchParams();
  const requestedTab = searchParams.get("tab");
  const [activeTab, setActiveTab] = useState(() =>
    TABS.some((tab) => tab.id === requestedTab) ? requestedTab : "gap_assessment",
  );
  const navigate = useNavigate();
  const { currentUser } = useApp();
  const allowedModules = currentUser?.modules || [];
  const isTabLocked = (id) => !allowedModules.includes(TAB_MODULE[id]);

  useEffect(() => {
    if (requestedTab && TABS.some((tab) => tab.id === requestedTab) && !isTabLocked(requestedTab)) {
      setActiveTab(requestedTab);
    }
  }, [requestedTab, allowedModules]);

  useEffect(() => {
    const onSetTab = (event) => {
      const tab = event.detail?.tab;
      if (tab && TABS.some((item) => item.id === tab) && !isTabLocked(tab)) {
        setActiveTab(tab);
      }
    };
    window.addEventListener("live-tour:set-response-tab", onSetTab);
    return () => window.removeEventListener("live-tour:set-response-tab", onSetTab);
  }, [allowedModules]);

  return (
    <div className="space-y-6 animate-fade-in">
      <div data-tour-id="tour-responses-header" className="flex items-center gap-3 min-w-0">
        <div className="relative flex-shrink-0">
          <div
            className="absolute inset-0 rounded-xl bg-gradient-to-br from-indigo-500 to-violet-600 blur-md opacity-35 scale-110"
            aria-hidden
          />
          <div
            className="relative w-10 h-10 rounded-xl flex items-center justify-center shadow-lg shadow-indigo-500/20 ring-1 ring-white/40"
            style={{ background: "linear-gradient(135deg, #6366f1 0%, #4f46e5 100%)" }}
          >
            <Eye size={18} className="text-white drop-shadow-sm" />
          </div>
        </div>
        <div className="min-w-0">
          <h1 className="text-2xl font-bold text-slate-900 tracking-tight">
            User Form Responses
          </h1>
          <p className="text-sm text-slate-500 mt-1">
            Monitor every answer users submitted across Gap Assessment, ROPA, PIA,
            and TPRM. Click <strong>View</strong> on any row to see the full
            question-and-answer detail.
          </p>
        </div>
      </div>

      <div
        data-tour-id="tour-responses-tabs"
        className="flex flex-wrap gap-2 border-b border-slate-200 pb-1"
      >
        {TABS.map((tab) => {
          const Icon = tab.icon;
          const active = activeTab === tab.id;
          const locked = isTabLocked(tab.id);
          return (
            <button
              key={tab.id}
              type="button"
              data-tour-id={`tour-responses-tab-${tab.id}`}
              onClick={() => !locked && setActiveTab(tab.id)}
              disabled={locked}
              title={
                locked
                  ? `${tab.label} is not included in your license`
                  : undefined
              }
              className={`flex items-center gap-2 px-4 py-2.5 text-sm font-medium rounded-t-lg border-b-2 transition-colors ${
                locked
                  ? "border-transparent text-slate-300 cursor-not-allowed"
                  : active
                    ? "border-indigo-600 text-indigo-700 bg-indigo-50/60"
                    : "border-transparent text-slate-500 hover:text-slate-800 hover:bg-slate-50"
              }`}
            >
              <Icon
                size={16}
                className={
                  locked
                    ? "text-slate-300"
                    : active
                      ? tab.iconColor
                      : "text-slate-400"
                }
              />
              {tab.label}
            </button>
          );
        })}
      </div>

      <p className="text-xs text-slate-500 -mt-2">
        {TABS.find((t) => t.id === activeTab)?.description}
      </p>

      <div data-tour-id="tour-responses-table">
        {isTabLocked(activeTab) ? (
          <div className="relative overflow-hidden rounded-2xl border border-indigo-100 bg-gradient-to-b from-indigo-50/70 via-white to-white py-16 px-6 flex flex-col items-center justify-center gap-3 text-center">
            <div className="pointer-events-none absolute -top-16 -right-16 w-56 h-56 rounded-full bg-gradient-to-br from-indigo-200/40 to-fuchsia-200/30 blur-2xl" />
            <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-indigo-500 via-violet-500 to-fuchsia-500 flex items-center justify-center shadow-lg shadow-indigo-500/30 ring-4 ring-white/60">
              <Crown size={20} className="text-white" strokeWidth={2.25} />
            </div>
            <div className="relative space-y-1">
              <p className="text-sm font-semibold text-slate-800">
                {TABS.find((t) => t.id === activeTab)?.label} isn't included in
                your plan
              </p>
              <p className="text-xs text-slate-500 max-w-sm mx-auto leading-relaxed">
                Upgrade your organization's license to view submitted{" "}
                {TABS.find((t) => t.id === activeTab)?.label} responses and
                unlock this module for your team.
              </p>
            </div>
            <button
              type="button"
              onClick={() => navigate("/tenant/license")}
              className="relative group inline-flex items-center gap-1.5 px-4 py-2 rounded-full text-xs font-semibold text-white bg-gradient-to-r from-indigo-600 to-violet-600 hover:from-indigo-500 hover:to-violet-500 shadow-md shadow-indigo-200/80 transition-all"
            >
              Upgrade License
              <ArrowUpRight
                size={13}
                className="transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5"
              />
            </button>
          </div>
        ) : (
          <>
            {activeTab === "gap_assessment" && <GapAssessmentResponsesPanel />}
            {activeTab === "ropa" && (
              <FormSubmissionsPage
                embedded
                config={withAdminMonitorConfig(ROPA_CONFIG)}
              />
            )}
            {activeTab === "pia" && (
              <FormSubmissionsPage
                embedded
                config={withAdminMonitorConfig(PIA_CONFIG)}
              />
            )}
            {activeTab === "tprm" && (
              <FormSubmissionsPage
                embedded
                config={withAdminMonitorConfig(TPRM_CONFIG)}
              />
            )}
          </>
        )}
      </div>
    </div>
  );
}
